import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import { Add, Edit, Visibility } from '@mui/icons-material';
import profileAPI from '../services/ProfileAPI.js';

const Customers = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  // ✅ FIX: Load customer data using ProfileAPI
  const loadData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await profileAPI.getProfilesByType(
        'Customer', // profileType
        1, // companyId
        {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          search: globalFilter,
          sortBy: sorting[0]?.id || 'name',
          sortDirection: sorting[0]?.desc ? 'desc' : 'asc'
        }
      );

      if (result.success && result.data) {
        setData(result.data);
        setTotalCount(result.pagination?.totalCount || result.data.length);
      } else {
        setError(result.message || 'Failed to load customers');
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customer data');
    }
    
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting]);

  // Define table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'code',
      header: 'Customer Code',
      size: 120,
    },
    {
      accessorKey: 'name',
      header: 'Customer Name',
      size: 200,
    },
    {
      accessorKey: 'primaryEmail',
      header: 'Email',
      size: 180,
    },
    {
      accessorKey: 'primaryPhone',
      header: 'Phone',
      size: 140,
    },
    {
      accessorKey: 'city',
      header: 'City',
      size: 120,
    },
    {
      accessorKey: 'currentBalance',
      header: 'Balance',
      size: 130,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <Typography
            variant="body2"
            color={value < 0 ? 'error' : 'success'}
            fontWeight="medium"
          >
            {new Intl.NumberFormat('en-PK', {
              style: 'currency',
              currency: 'PKR'
            }).format(value || 0)}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() || 'Active'}
          size="small"
          color={cell.getValue() === 'Active' ? 'success' : 'default'}
        />
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleView(row.original)}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(row.original)}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ], []);

  // ✅ FIX: Navigate to customer profile page
  const handleView = (customer) => {
    navigate(`/erp/accounts/customers/${customer.id}`);
  };

  const handleEdit = (customer) => {
    navigate(`/erp/accounts/customers/${customer.id}/edit`);
  };

  const handleAddNew = () => {
    navigate('/erp/accounts/customers/new');
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
        <Button onClick={loadData} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h5">Customers</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your customer profiles
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddNew}
              sx={{
                bgcolor: '#800000',
                '&:hover': { bgcolor: '#600000' }
              }}
            >
              Add New Customer
            </Button>
          </Box>

          <MaterialReactTable
            columns={columns}
            data={data}
            manualPagination
            manualSorting
            manualFiltering
            rowCount={totalCount}
            state={{ pagination, globalFilter, sorting, isLoading }}
            onPaginationChange={setPagination}
            onGlobalFilterChange={setGlobalFilter}
            onSortingChange={setSorting}
            enableRowSelection={false}
            initialState={{ density: 'compact' }}
            muiTableProps={{
              sx: {
                '& .MuiTableCell-root': {
                  fontSize: '0.875rem'
                }
              }
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: '#f8fafc',
                color: '#374151',
                fontWeight: 'bold'
              }
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Customers;